<script lang="ts">
  import { filterTransactions, calculateSummary } from '../lib/analytics';
  import SpendingLineChart from './SpendingLineChart.svelte';
  import IncomePieChart from './IncomePieChart.svelte';
  import ExpensesPieChart from './ExpensesPieChart.svelte';
  import VendorPieChart from './VendorPieChart.svelte';
  import SankeyDiagram from './SankeyDiagram.svelte';
  import BudgetVsActualChart from './BudgetVsActualChart.svelte';
  import CategorySpendingTrendsChart from './CategorySpendingTrendsChart.svelte';
  import FiltersInline from './FiltersInline.svelte';
  import { Button } from './common';
  import type { Summary, Transaction, FilterCriteria } from '../types/transaction';

  export type Props = {
    allTransactions: Transaction[];
  };

  let {
    allTransactions
  }: Props = $props();

  // Local filter state for visualizations - needs to be a deeply reactive object
  let filters = $state<FilterCriteria>({
    startDate: '',
    endDate: '',
    category: [],
    person: '',
    institution: '',
    search: '',
    minAmount: '',
    maxAmount: '',
    showExcluded: false
  });

  let isFiltersExpanded = $state(false);

  // Apply local filters to allTransactions and calculate summary
  let filteredTransactions = $derived(filterTransactions(allTransactions, filters));
  let filteredSummary = $derived(calculateSummary(filteredTransactions));

  function handleFilterChange() {
    // Filters are automatically reactive with $derived - no need to do anything
  }

  function toggleFilters() {
    isFiltersExpanded = !isFiltersExpanded;
  }

  function clearFilters() {
    filters = {
      startDate: '',
      endDate: '',
      category: [],
      person: '',
      institution: '',
      search: '',
      minAmount: '',
      maxAmount: '',
      showExcluded: false
    };
  }

  function setQuickDateRange(days: number | 'ytd' | 'all') {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];

    if (days === 'all') {
      filters.startDate = '';
      filters.endDate = '';
    } else if (days === 'ytd') {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      filters.startDate = yearStart.toISOString().split('T')[0];
      filters.endDate = endDate;
    } else {
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - days);
      filters.startDate = startDate.toISOString().split('T')[0];
      filters.endDate = endDate;
    }
  }
</script>

<div class="visualizations">
  <div class="filters-header">
    <h2>Visualizations</h2>
    <div class="filter-actions">
      <Button variant="secondary" size="sm" onclick={toggleFilters}>
        {isFiltersExpanded ? 'Hide Filters' : 'Show Filters'}
      </Button>
      {#if isFiltersExpanded}
        <Button variant="secondary" size="sm" onclick={clearFilters}>
          Clear Filters
        </Button>
      {/if}
    </div>
  </div>

  <!-- Quick Date Range Filters -->
  <div class="quick-filters">
    <span class="quick-filters-label">Quick Date Range:</span>
    <div class="quick-filter-buttons">
      <button class="quick-filter-btn" onclick={() => setQuickDateRange(30)}>Last 30 Days</button>
      <button class="quick-filter-btn" onclick={() => setQuickDateRange(90)}>Last 90 Days</button>
      <button class="quick-filter-btn" onclick={() => setQuickDateRange(180)}>Last 6 Months</button>
      <button class="quick-filter-btn" onclick={() => setQuickDateRange('ytd')}>Year to Date</button>
      <button class="quick-filter-btn" onclick={() => setQuickDateRange('all')}>All Time</button>
    </div>
  </div>

  {#if isFiltersExpanded}
    <FiltersInline
      bind:filters
      isExpanded={isFiltersExpanded}
      onFilterChange={handleFilterChange}
      autoInitializeCategories={false}
    />
  {/if}

  {#if filteredSummary && (filteredSummary.totalTransactions > 0 || Object.keys(filteredSummary.byCategory || {}).length > 0)}
    <!-- Time-Based Analysis -->
    <div class="chart-section">
      <h3 class="section-title">Time-Based Analysis</h3>
      <div class="charts-grid two-col">
        <div class="chart-card">
          <h4>Spending Over Time</h4>
          <SpendingLineChart summary={filteredSummary} />
        </div>

        <div class="chart-card">
          <h4>Category Spending Trends</h4>
          <CategorySpendingTrendsChart summary={filteredSummary} transactions={filteredTransactions} />
        </div>
      </div>
    </div>

    <!-- Income & Expense Analysis -->
    <div class="chart-section">
      <h3 class="section-title">Income & Expense Analysis</h3>
      <div class="charts-grid three-col">
        <div class="chart-card">
          <h4>Income Breakdown</h4>
          <IncomePieChart summary={filteredSummary} transactions={filteredTransactions} />
        </div>

        <div class="chart-card">
          <h4>Expense Breakdown</h4>
          <ExpensesPieChart summary={filteredSummary} />
        </div>

        <div class="chart-card">
          <h4>Top Vendors</h4>
          <VendorPieChart summary={filteredSummary} />
        </div>
      </div>
    </div>

    <!-- Budget & Flow -->
    <div class="chart-section">
      <h3 class="section-title">Budget & Flow</h3>
      <div class="charts-grid">
        <div class="chart-card">
          <h4>Budget vs Actual</h4>
          <BudgetVsActualChart summary={filteredSummary} />
        </div>

        <div class="chart-card full-width">
          <h4>Money Flow</h4>
          <SankeyDiagram summary={filteredSummary} />
        </div>
      </div>
    </div>
  {:else}
    <div class="empty-state">
      <p>Loading visualizations...</p>
    </div>
  {/if}
</div>

<style lang="scss">
  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);

    h2 {
      margin: 0;
      font-size: var(--font-size-3xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }

    .filter-actions {
      display: flex;
      gap: var(--spacing-sm);
    }
  }

  .quick-filters {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-2xl);
    padding: var(--spacing-md);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
    flex-wrap: wrap;

    .quick-filters-label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-secondary);
    }

    .quick-filter-buttons {
      display: flex;
      gap: var(--spacing-sm);
      flex-wrap: wrap;
    }

    .quick-filter-btn {
      padding: var(--spacing-xs) var(--spacing-md);
      background: var(--color-background);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      cursor: pointer;
      transition: all var(--transition-fast);

      &:hover {
        background: var(--color-primary);
        color: white;
        border-color: var(--color-primary);
        transform: translateY(-1px);
        box-shadow: var(--shadow-sm);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .chart-section {
    margin-bottom: var(--spacing-4xl);

    .section-title {
      margin: 0 0 var(--spacing-lg) 0;
      font-size: var(--font-size-2xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
      border-bottom: 2px solid var(--color-border);
      padding-bottom: var(--spacing-sm);
    }
  }

  .charts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: var(--spacing-xl);

    &.two-col {
      grid-template-columns: repeat(2, 1fr);
    }

    &.three-col {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .chart-card {
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    transition: all var(--transition-fast);

    &:hover {
      border-color: var(--color-primary);
      box-shadow: var(--shadow-lg);
    }

    h4 {
      margin: 0 0 var(--spacing-lg) 0;
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-semibold);
      color: var(--color-text-primary);
    }
  }

  .chart-card.full-width {
    grid-column: 1 / -1;
  }

  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    color: var(--color-text-muted);

    p {
      font-size: var(--font-size-lg);
      margin: 0;
    }
  }

  @media (max-width: 1200px) {
    .charts-grid.three-col {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .charts-grid,
    .charts-grid.two-col,
    .charts-grid.three-col {
      grid-template-columns: 1fr;
    }

    .filters-header h2 {
      font-size: var(--font-size-2xl);
    }

    .quick-filters {
      flex-direction: column;
      align-items: flex-start;

      .quick-filter-buttons {
        width: 100%;
      }

      .quick-filter-btn {
        flex: 1;
        text-align: center;
      }
    }

    .chart-section .section-title {
      font-size: var(--font-size-xl);
    }
  }
</style>
